import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { CURP_LENGTH, isValidCurp, normalizeCurp } from '../../utils/curp';
import { CurpBox } from './sales.styles';

// Campo de CURP SIN ESTADO CONTROLADO: el valor vive solo en el <input> (el
// DOM) y en el estado del componente solo queda si el formato es correcto, no
// el CURP. Así no pasa por el estado de React, ni por localStorage, y la
// pantalla lo muestra enmascarado (type=password) salvo que la persona pulse
// "Ver" para compararlo con la identificación.
//
// El padre lo lee con ref.current.getValue() justo al enviar y lo borra con
// ref.current.clear().
const CurpField = forwardRef(function CurpField({ id, label, disabled, serverError }, ref) {
  const inputRef = useRef(null);
  // 'empty' | 'typing' | 'valid' | 'invalid'
  const [status, setStatus] = useState('empty');
  const [reveal, setReveal] = useState(false);

  const evaluate = (finished) => {
    const value = normalizeCurp(inputRef.current ? inputRef.current.value : '');
    if (!value) return 'empty';
    if (isValidCurp(value)) return 'valid';
    return finished ? 'invalid' : 'typing';
  };

  useImperativeHandle(ref, () => ({
    getValue: () => normalizeCurp(inputRef.current ? inputRef.current.value : ''),
    clear: () => {
      if (inputRef.current) inputRef.current.value = '';
      setReveal(false);
      setStatus('empty');
    },
    // Marca el error de formato (si lo hay) y devuelve si es válido.
    validate: () => {
      const next = evaluate(true);
      setStatus(next);
      return next === 'valid';
    },
    focus: () => inputRef.current && inputRef.current.focus(),
  }));

  const handleInput = (event) => {
    const input = event.target;
    const cleaned = input.value.toUpperCase().replace(/\s+/g, '');
    if (cleaned !== input.value) {
      const caret = input.selectionStart;
      input.value = cleaned;
      try { input.setSelectionRange(caret, caret); } catch (e) { /* tipo sin selección */ }
    }
    setStatus(evaluate(status === 'invalid'));
  };

  const hintId = `${id}-hint`;
  const message = serverError
    || (status === 'invalid'
      ? `El CURP debe tener ${CURP_LENGTH} caracteres con el formato oficial (4 letras, 6 dígitos de fecha, H/M/X, estado, consonantes, homoclave y dígito).`
      : '');

  return (
    <CurpBox>
      <label htmlFor={id}>{label}</label>
      <div className="curp-input">
        <input
          id={id}
          ref={inputRef}
          type={reveal ? 'text' : 'password'}
          name={`${id}-sin-guardar`}
          defaultValue=""
          maxLength={CURP_LENGTH + 4}
          autoComplete="off"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          disabled={disabled}
          aria-invalid={status === 'invalid' || Boolean(serverError)}
          aria-describedby={hintId}
          onInput={handleInput}
          onBlur={() => { setReveal(false); setStatus(evaluate(true)); }}
        />
        <button
          type="button"
          className="reveal"
          // mouseDown no debe quitar el foco del campo (el onBlur lo vuelve a enmascarar)
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setReveal((r) => !r)}
          aria-pressed={reveal}
          disabled={disabled}
        >
          {reveal ? 'Ocultar' : 'Ver'}
        </button>
      </div>
      <div id={hintId} className={`curp-hint ${message ? 'bad' : status === 'valid' ? 'good' : ''}`} role={message ? 'alert' : undefined}>
        {message || (status === 'valid' ? '✓ Formato correcto' : 'Se muestra oculto. Pulsa «Ver» para compararlo con la identificación.')}
      </div>
    </CurpBox>
  );
});

export default CurpField;
